"use client";

import { useRef, useEffect } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const trendingProducts = [
{
  id: "t1",
  title: "ABC Wonderland Coloring Book",
  subtitle: "Alphabet + Animals",
  price: "$12.99",
  originalPrice: "$18.99",
  rating: 4.9,
  reviews: 842,
  tag: "BESTSELLER",
  tagColor: "#F4C842",
  ageRange: "Ages 2–5",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15b8cab21-1767790341048.png",
  alt: "ABC Wonderland coloring book with illustrated animals and alphabet letters for young children"
},
{
  id: "t2",
  title: "123 Rainbow Numbers",
  subtitle: "Count & Color",
  price: "$10.99",
  originalPrice: "$15.99",
  rating: 5.0,
  reviews: 1203,
  tag: "TOP RATED",
  tagColor: "#A8DADC",
  ageRange: "Ages 3–6",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17cbcdb51-1764727987464.png",
  alt: "Rainbow numbers counting and coloring activity book for preschool and kindergarten children"
},
{
  id: "t3",
  title: "Magical Creatures Coloring",
  subtitle: "Fantasy & Imagination",
  price: "$14.99",
  originalPrice: "$19.99",
  rating: 4.8,
  reviews: 567,
  tag: "NEW",
  tagColor: "#C8B6E2",
  ageRange: "Ages 4–8",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fd32f24b-1767494495174.png",
  alt: "Magical creatures coloring book featuring unicorns, dragons, and fairy tale characters"
},
{
  id: "t4",
  title: "Tracing & Writing Starter",
  subtitle: "Pencil Control + Letters",
  price: "$9.99",
  originalPrice: "$13.99",
  rating: 4.9,
  reviews: 934,
  tag: "POPULAR",
  tagColor: "#FFB5A7",
  ageRange: "Ages 3–5",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1983cddaa-1771051735305.png",
  alt: "Letter tracing and writing practice book for early learners and preschoolers"
},
{
  id: "t5",
  title: "Ocean World Coloring Set",
  subtitle: "Sea Life + Colors",
  price: "$16.99",
  originalPrice: "$22.99",
  rating: 4.7,
  reviews: 421,
  tag: "BUNDLE",
  tagColor: "#B8E0FF",
  ageRange: "Ages 4–8",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19d5afcf1-1768513346420.png",
  alt: "Ocean world coloring book set featuring fish, whales, and underwater adventure scenes"
}];


export default function TrendingSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("hidden-before-reveal");
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll(".reveal")?.forEach((el) => {
      el?.classList?.add("hidden-before-reveal");
      observer?.observe(el);
    });
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-cream overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="reveal flex flex-col sm:flex-row justify-between items-start sm:items-end mb-10 gap-4">
          <div>
            <span className="text-xs font-black tracking-widest uppercase text-plum-light bg-plum-100 px-3 py-1 rounded-full">
              ✨ Trending Now
            </span>
            <h2 className="font-fraunces text-4xl md:text-5xl font-black text-plum mt-3 leading-tight">
              Most Loved by
              <span className="italic text-plum-light block">Little Readers</span>
            </h2>
          </div>
          <Link href="/products" className="btn-secondary px-6 py-3 text-sm flex items-center gap-2 shrink-0">
            View All Books
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>

        {/* Horizontal scroll */}
        <div className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-none" style={{ scrollbarWidth: "none" }}>
          {trendingProducts?.map((product, i) =>
          <div
            key={product?.id}
            className="reveal product-card flex-none w-64 bg-white rounded-3xl shadow-dreamy overflow-hidden snap-start"
            style={{ animationDelay: `${i * 0.1}s` }}>
            
              {/* Image */}
              <div className="relative h-64 overflow-hidden bg-plum-50">
                <AppImage
                src={product?.image}
                alt={product?.alt}
                fill
                priority={false}
                sizes="256px"
                className="object-cover product-img" />
              
                {/* Tag */}
                <div
                className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-black text-plum shadow-sm"
                style={{ background: product?.tagColor }}>
                
                  {product?.tag}
                </div>
                {/* Age badge */}
                <div className="absolute top-3 right-3 age-badge">{product?.ageRange}</div>
                {/* Wishlist */}
                <button
                className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white transition-colors shadow"
                aria-label={`Add ${product?.title} to wishlist`}>
                
                  <Icon name="HeartIcon" size={16} className="text-rose" />
                </button>
              </div>

              {/* Info */}
              <div className="p-4">
                <h3 className="font-fraunces font-bold text-plum text-base leading-tight mb-0.5">{product?.title}</h3>
                <p className="text-xs text-plum/55 mb-2">{product?.subtitle}</p>
                <div className="flex items-center gap-1 mb-3">
                  <span className="stars text-sm">{"★"?.repeat(Math.floor(product?.rating))}</span>
                  <span className="text-xs font-bold text-plum">{product?.rating}</span>
                  <span className="text-xs text-plum/40">({product?.reviews})</span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-black text-plum text-lg">{product?.price}</span>
                    <span className="text-xs text-plum/40 line-through ml-1">{product?.originalPrice}</span>
                  </div>
                  <Link
                  href="/products"
                  className="btn-primary text-xs px-4 py-2 flex items-center gap-1">
                  
                    <Icon name="ShoppingCartIcon" size={14} variant="solid" />
                    Add
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}